<template>
  <div>
    <el-empty v-if="!accounts.list.length">
      <el-button type="primary" @click="dialogState.visible = true">{{ t("accounts.createRefundAccount") }}</el-button>
    </el-empty>
    <div v-else class="p-4">
      <div class="d-flex flex-wrap align-items-center justify-content-between">
        <div>
        </div>
        <div>
          <el-button
            type="primary"
            @click="onClickSetting"
          >
            {{ t("global.setting") }}
          </el-button>
        </div>
      </div>
      <BankDialogConfirmData v-model="dialogState.data" />
    </div>

    <el-dialog
      v-model="dialogState.visible"
      :width="isMobile ? '90%' : '800px'"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
      :append-to-body="true"
      :destroy-on-close="true"
    >
      <template #header>
        <span v-if="active === 0">
          {{ titles[active] }}
        </span>
        <span
          v-else
          style="cursor: pointer"
          @click="prev"
        >
          <i class="fa fa-lg fa-angle-left mr-2"></i>
          {{ titles[active] }}
        </span>
      </template>

      <el-form
        ref="formDataRef"
        :model="dialogState.data"
        :rules="rules"
        :label-position="isMobile ? 'top' : 'right'"
        label-width="200px"
      >
        <div v-if="active === 0">
          <BankDialogUserForm
            v-model="dialogState.data"
            :iso-state="isoState"
          />
        </div>
        <div v-else-if="active === 1">
          <BankDialogAccountForm
            v-model="dialogState.data"
            :banks="banks"
            @change:bank="handleBank"
          />
        </div>
        <div v-else>
          <BankDialogConfirmData v-model="dialogState.data" />
        </div>
      </el-form>

      <template #footer>
        <span class="dialog-footer">
          <el-button
            :loading="dialogState.loading"
            @click="onClickSetting"
          >
            {{ t("global.cancel") }}
          </el-button>

          <el-button
            v-if="active < 2"
            type="primary"
            @click="next"
          >
            {{ t("global.next") }}
            <i class="fa fa-lg fa-angle-right ml-2"></i>
          </el-button>
          <el-button
            v-else
            type="primary"
            :loading="dialogState.loading"
            @click="handleSave"
          >
            {{ t("global.submit") }}
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { reactive, ref, computed, onMounted } from 'vue'
import { useI18n } from "vue-i18n"
import { notificationErrors, dateFormat, breakpoint, checkValidCurrencyStr } from "../../utils"
import { ElNotification } from 'element-plus'
import {
  _api_getAccountRefunds,
  _api_postAccountRefund,
  _api_getAccountRefund,
  _api_putAccountRefund,
  _api_getTwBanks
} from "../../api/index.js"
import BankDialogUserForm from "./components/BankDialogUserForm.vue"
import BankDialogAccountForm from "./components/BankDialogAccountForm.vue"
import BankDialogConfirmData from "./components/BankDialogConfirmData.vue"

const props = defineProps({
  country: { type: String, default: "TW" }
})
const { t } = useI18n()

const isMobile = computed(() => breakpoint.value.xs || breakpoint.value.sm)
const identityEncrypt = computed(() =>
  accounts.list.length ? accounts.list.find((el) => el.is_default) : null
)
const titles = computed(() => [
  t("accounts.userInfo"),
  t("accounts.createBankAccount"),
  t("accounts.confirmInfo"),
])

const accounts = reactive({
  list: [],
  data: {},
  paging: null,
})

const banks = reactive({
  dict: {},
  list: [],
  branch: {},
  branchList: [],
  loading: false,
  item: {},
  branchItem: {},
})

const formDataRef = ref()
const active = ref(0)

const dialogState = reactive({
  visible: false,
  loading: false,
  account_id: null,
  data: {
    name: null,
    identity_no: null,
    // identity_img1_file: null,
    // identity_img2_file: null,
    birthday: null,
    iso_country: props.country,
    iso_province: null,
    iso_city: null,
    iso_region: null,
    postcode: null,
    province: null,
    city: null,
    region: null,
    address: null,
    // to_iso_province: null,
    // to_iso_city: null,
    // to_iso_region: null,
    // to_postcode: null,
    // to_province: null,
    // to_city: null,
    // to_region: null,
    // to_address: null,
    bank_code: null,
    bank_branch_code: null,
    bank_name: null,
    bank_branch_name: null,
    account_no: null,
    account_name: null,
    // account_img_file: null,
    // sameAsBuyer: false,
  },
})

const rules = reactive({
  name: [{ required: true, message: t("errors.pleaseInput", [t("users.realName")]), trigger: "blur" }],
  identity_no: [{ required: true, message: t("errors.pleaseInput", [t("users.identityNo")]), trigger: "blur" }],
  iso_province: [{ required: true, message: t("errors.pleaseSelect", [t("users.province")]), trigger: "change" }],
  iso_city: [{ required: true, message: t("errors.pleaseSelect", [t("users.city")]), trigger: "change" }],
  address: [{ required: true, message: t("errors.pleaseInput", [t("users.address")]), trigger: "change" }],
  // to_iso_province: [{ required: true, message: t("errors.pleaseSelect", [t("users.province")]), trigger: "change" }],
  // to_iso_city: [{ required: true, message: t("errors.pleaseSelect", [t("users.city")]), trigger: "change" }],
  // to_address: [{ required: true, message: t("errors.pleaseInput", [t("users.address")]), trigger: "change" }],
  birthday: [{ required: true, message: t("errors.pleaseSelect", [t("users.birthday")]), trigger: "change" }],
  bank_name: [{ required: true, message: t("errors.pleaseSelect", [t("bankInfo.bank")]), trigger: "change" }],
  bank_branch_name: [{ required: true, message: t("errors.pleaseSelect", [t("bankInfo.branch")]), trigger: "change" }],
  account_name: [{ required: true, message: t("errors.pleaseInput", [t("bankInfo.accountName")]), trigger: "blur" }],
  account_no: [{ required: true, message: t("errors.pleaseInput", [t("bankInfo.accountNo")]), trigger: "blur" }],
  // account_img_file: [
  //   { required: true, message: t("errors.PleaseUpload", [t("bankInfo.accountImg")]), trigger: "blur" },
  // ],
  // identity_img1_file: [
  //   { required: true, message: t("errors.PleaseUpload", [t("settings.identityImgTip1")]), trigger: "blur" },
  // ],
  // identity_img2_file: [
  //   { required: true, message: t("errors.PleaseUpload", [t("settings.identityImgTip2")]), trigger: "blur" },
  // ],
})

const next = async () => {
  if (!formDataRef.value) return
  await formDataRef.value.validate((valid, fields) => {
    if (valid) {
      active.value++
    } else {
      console.log("error next!", fields)
    }
  })
}
const prev = () => {
  active.value--
}
const onClickSetting = () => {
  dialogState.visible = !dialogState.visible
  if (!dialogState.visible && formDataRef.value) {
    formDataRef.value.resetFields()
  }
}
const handleSave = async () => {
  window.fullPageLoading("show")
  dialogState.loading = true
  try {
    if (!dialogState.data.iso_region) {
      delete dialogState.data.iso_region
    }
    if (dialogState.account_id) {
      await _api_putAccountRefund(dialogState.account_id, dialogState.data)
    } else {
      await _api_postAccountRefund(dialogState.data)
    }
    dialogState.visible = false
    active.value = 0
    getData()

  } catch (err) {
    // console.log("handleSave: ", err.response.data)
    notificationErrors(err.response.data)
  } finally {
    window.fullPageLoading("hide")
    dialogState.loading = false
  }
}

const getData = async () => {
  window.fullPageLoading("show")
  try {
    const resp = await _api_getAccountRefunds()
    // console.log("getData: ", resp)
    accounts.list = resp.data.data
    accounts.paging = resp.data.paging
    if (identityEncrypt.value) {
      await handleSetting(identityEncrypt.value.id)
    }

  } catch (err) {
    notificationErrors(err)
  } finally {
    window.fullPageLoading("hide")
  }
}
const handleSetting = async (id) => {
  try {
    const resp = await _api_getAccountRefund(id, { decrypt_cust_data: true })
    accounts.data = resp.data
    // console.log("handleSetting", accounts.data)
    // console.log("dialogState", dialogState)
    dialogState.account_id = accounts.data.id
    Object.assign(dialogState.data, {
      name: accounts.data.cust_data.buyer_name,
      identity_no: accounts.data.cust_data.identity_no,
      birthday: accounts.data.cust_data.birthday,
      province: accounts.data.cust_data.province,
      city: accounts.data.cust_data.city,
      region: accounts.data.cust_data.region,
      iso_country: accounts.data.cust_data.iso_country,
      iso_city: accounts.data.cust_data.iso_city,
      iso_province: accounts.data.cust_data.iso_province,
      iso_region: accounts.data.cust_data.iso_region,
      postcode: accounts.data.cust_data.postcode,
      address: accounts.data.cust_data.address,
      ...accounts.data.cust_data.bank_info,
    })
    if (dialogState.data.bank_code) {
      banks.item = banks.list.find((el) => el.code == dialogState.data.bank_code)
      await handleBank({ code: dialogState.data.bank_code })
      banks.branchItem = banks.branchList.find((el) => el.subcode == dialogState.data.bank_branch_code)
    }
  } catch (err) {
    notificationErrors(err)
  }
}

const getBank = async () => {
  banks.loading = true
  try {
    const resp = await _api_getTwBanks()
    banks.dict = resp.data
    banks.list = Object.keys(banks.dict).map((id) => ({ ...(banks.dict[id]), id: id }))
  } catch (err) {
    notificationErrors(err)
  } finally {
    banks.loading = false
  }
}
const handleBank = async (bank) => {
  banks.loading = true
  try {
    const resp = await _api_getTwBanks({ code: bank.code })
    // console.log("getBank: ", resp)
    banks.branch = resp.data
    banks.branchList = Object.keys(banks.branch).map((id) => ({ ...(banks.branch[id]), id: id }))
  } catch (err) {
    notificationErrors(err)
  } finally {
    banks.loading = false
  }
}

const isoState = reactive({
  countryMap: {},
  province: {},
  provinceList: [],
  loading: false,
})
const setCountryMap = (country) => {
  if (country) {
    const country_code = country.toLowerCase()
    isoState.loading = true
    axios({
      method: 'GET',
      url: "/api/v1/data/iso-3166-ext/"+country_code,
      headers: { 'Content-Type': 'application/json' },
    }).then((res) => {
      // console.log(res)
      isoState.countryMap = res.data
      isoState.province = res.data.province
      isoState.provinceList = Object.keys(isoState.province).map((iso) => ({ ...isoState.province[iso], iso }))
      isoState.loading = false
    })
    .catch((err) => {
      isoState.loading = false
      return Promise.reject(err.response)
    })

  } else {
    isoParams.countryMap = null
  }
}

onMounted(() => {
  getBank()
  setCountryMap(props.country)
  getData()
})
</script>